import { useEffect, useState } from 'react'
import supabase from '../supabaseClient'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'

type CsvJobStatus = {
  id: string
  status: string
  total_rows: number
  processed_rows: number
  added: number
  skipped: number
  failed: number
  error_message?: string | null
  updated_at?: string | null
}

type CsvImportError = {
  id: string
  row_index: number
  title: string | null
  reason: string | null
  raw_row?: {
    row?: string[]
    candidates?: {
      id: number
      name: string
      coverUrl?: string | null
      first_release_date?: number | null
      score?: number
    }[]
  } | string[] | null
}

type IgdbSearchResult = {
  id: number
  name: string
  cover?: { url?: string | null }
  first_release_date?: number | null
}

const buildSearchVariants = (value: string) => {
  const trimmed = value.trim()
  if (!trimmed) return []
  const base = trimmed
  const noSymbols = trimmed
    .replace(/[™®©]/g, '')
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
  const noBracketed = noSymbols.replace(/\s*[[{(].*?[\]})]\s*/g, ' ').trim()
  const beforeColon = noBracketed.split(':')[0]?.trim()
  const beforeDash = noBracketed.split(' - ')[0]?.trim()
  const cleaned = noBracketed.replace(/[^a-zA-Z0-9\s:'-]/g, '').trim()

  const variants = [base, noSymbols, noBracketed, beforeColon, beforeDash, cleaned]
    .filter(Boolean)
    .map((item) => item.trim())

  return Array.from(new Set(variants)).filter(Boolean)
}

const toCoverUrl = (rawUrl?: string | null) => {
  if (!rawUrl) return null
  const withProtocol = rawUrl.startsWith('http') ? rawUrl : `https:${rawUrl}`
  return withProtocol.replace('t_thumb', 't_cover_small')
}

const getStoredJobId = () => {
  try {
    return localStorage.getItem('csvImportLastJobId')
  } catch {
    return null
  }
}

const clearStoredJobId = () => {
  try {
    localStorage.removeItem('csvImportLastJobId')
    localStorage.removeItem('csvImportDismissedJobId')
  } catch {
    // ignore
  }
}

const getDismissedJobId = () => {
  try {
    return localStorage.getItem('csvImportDismissedJobId')
  } catch {
    return null
  }
}

const setDismissedJobId = (jobId: string) => {
  try {
    localStorage.setItem('csvImportDismissedJobId', jobId)
  } catch {
    // ignore
  }
}

const CsvImportStatusWidget = () => {
  const [jobStatus, setJobStatus] = useState<CsvJobStatus | null>(null)
  const [completedJob, setCompletedJob] = useState<CsvJobStatus | null>(null)
  const [errors, setErrors] = useState<CsvImportError[] | null>(null)
  const [errorsLoading, setErrorsLoading] = useState(false)
  const [showReviewModal, setShowReviewModal] = useState(false)
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>({})
  const [searchResults, setSearchResults] = useState<
    Record<string, IgdbSearchResult[]>
  >({})
  const [searchLoading, setSearchLoading] = useState<Record<string, boolean>>({})
  const [searchErrors, setSearchErrors] = useState<Record<string, string>>({})
  const [searchAttempts, setSearchAttempts] = useState<Record<string, boolean>>({})
  const [resolveLoading, setResolveLoading] = useState<Record<string, boolean>>({})
  const [resumeLoading, setResumeLoading] = useState(false)
  const [resumeMessage, setResumeMessage] = useState<string | null>(null)

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | null = null
    let isActive = true

    const fetchWithAuth = async (url: string) => {
      const {
        data: { session },
      } = await supabase.auth.getSession()
      const accessToken = session?.access_token
      if (!accessToken) return { ok: false, status: 401, data: null }
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      if (!response.ok) {
        return { ok: false, status: response.status, data: null }
      }
      return { ok: true, status: response.status, data: await response.json() }
    }

    const fetchStatus = async () => {
      try {
        const runningPayload = await fetchWithAuth(
          `${API_BASE_URL}/api/csv-imports/latest/running`
        )
        if (!isActive) return

        if (!runningPayload.ok) {
          if ([401, 403, 404].includes(runningPayload.status)) {
            clearStoredJobId()
            setJobStatus(null)
            setCompletedJob(null)
            return
          }
        } else if (runningPayload.data?.job) {
          setJobStatus(runningPayload.data.job as CsvJobStatus)
          setCompletedJob(null)
          return
        }

        setJobStatus(null)

        const lastJobId = getStoredJobId()
        if (!lastJobId) {
          setCompletedJob(null)
          return
        }

        const jobPayload = await fetchWithAuth(
          `${API_BASE_URL}/api/csv-imports/${lastJobId}`
        )
        if (!isActive) return
        if (!jobPayload.ok) {
          if ([401, 403, 404].includes(jobPayload.status)) {
            clearStoredJobId()
            setCompletedJob(null)
            return
          }
          setCompletedJob(null)
          return
        }

        const status = jobPayload.data?.status
        if (status === 'completed' || status === 'failed' || status === 'canceled') {
          setCompletedJob(jobPayload.data as CsvJobStatus)
        } else if (status === 'queued' || status === 'processing') {
          setJobStatus(jobPayload.data as CsvJobStatus)
          setCompletedJob(null)
        }
      } catch (error) {
        console.warn('[csv-import] widget status failed', error)
      }
    }

    fetchStatus()
    intervalId = setInterval(fetchStatus, 5000)

    return () => {
      isActive = false
      if (intervalId) clearInterval(intervalId)
    }
  }, [])

  const handleDismiss = (jobId?: string | null, persist = true) => {
    if (persist && jobId) {
      setDismissedJobId(jobId)
    }
    setJobStatus(null)
    setCompletedJob(null)
    setErrors(null)
  }

  const loadErrors = async (jobId: string) => {
    setErrorsLoading(true)
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession()
      const accessToken = session?.access_token
      if (!accessToken) return
      const response = await fetch(
        `${API_BASE_URL}/api/csv-imports/${jobId}/errors`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      )
      if (!response.ok) return
      const payload = await response.json()
      setErrors((payload?.errors as CsvImportError[]) || [])
    } catch (error) {
      console.warn('[csv-import] fetch errors failed', error)
    } finally {
      setErrorsLoading(false)
    }
  }

  const handleOpenReview = async () => {
    if (!completedJob) return
    if (!errors) {
      await loadErrors(completedJob.id)
    }
    setShowReviewModal(true)
  }

  const handleSearch = async (errorId: string, defaultTitle?: string | null) => {
    if (!completedJob) return
    const rawQuery = searchQueries[errorId] ?? defaultTitle ?? ''
    const variants = buildSearchVariants(rawQuery)
    if (!variants.length) return

    setSearchLoading((prev) => ({ ...prev, [errorId]: true }))
    setSearchErrors((prev) => ({ ...prev, [errorId]: '' }))
    setSearchAttempts((prev) => ({ ...prev, [errorId]: true }))
    try {
      const { data: { session } } = await supabase.auth.getSession()
      let results: IgdbSearchResult[] = []
      let lastErrorMessage = ''

      for (const variant of variants) {
        const sanitized = variant.replace(/"/g, '\\"')
        const response = await fetch(`${API_BASE_URL}/api/igdb/games`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
          },
          body: JSON.stringify({
            query: `search "${sanitized}"; fields id,name,first_release_date,cover.url; limit 10;`,
          }),
        })
        if (!response.ok) {
          const errorPayload = await response.json().catch(() => null)
          lastErrorMessage =
            errorPayload?.error || `IGDB search failed (${response.status})`
          continue
        }
        const payload = await response.json()
        if (!Array.isArray(payload)) {
          lastErrorMessage = 'IGDB search returned unexpected data.'
          continue
        }
        results = payload
        if (results.length > 0) break
      }

      if (!results.length && lastErrorMessage) {
        setSearchErrors((prev) => ({ ...prev, [errorId]: lastErrorMessage }))
      }

      setSearchResults((prev) => ({ ...prev, [errorId]: results }))
    } catch (error) {
      console.warn('[csv-import] igdb search failed', error)
      setSearchErrors((prev) => ({
        ...prev,
        [errorId]: 'IGDB search failed. Please try again.',
      }))
      setSearchResults((prev) => ({ ...prev, [errorId]: [] }))
    } finally {
      setSearchLoading((prev) => ({ ...prev, [errorId]: false }))
    }
  }

  const handleResolve = async (
    errorItem: CsvImportError,
    igdbId: number
  ) => {
    if (!completedJob) return
    setResolveLoading((prev) => ({ ...prev, [errorItem.id]: true }))
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession()
      const accessToken = session?.access_token
      if (!accessToken) return

      const response = await fetch(
        `${API_BASE_URL}/api/csv-imports/${completedJob.id}/resolve`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            rowIndex: errorItem.row_index,
            igdbId,
          }),
        }
      )
      if (!response.ok) return
      const payload = await response.json()
      if (payload?.job) {
        setCompletedJob(payload.job as CsvJobStatus)
      }
      setErrors((prev) =>
        prev ? prev.filter((item) => item.id !== errorItem.id) : prev
      )
      setSearchResults((prev) => {
        const next = { ...prev }
        delete next[errorItem.id]
        return next
      })
    } catch (error) {
      console.warn('[csv-import] resolve failed', error)
    } finally {
      setResolveLoading((prev) => ({ ...prev, [errorItem.id]: false }))
    }
  }

  const dismissedJobId = getDismissedJobId()
  const shouldShowCompleted =
    completedJob && completedJob.id !== dismissedJobId && !jobStatus

  if (!jobStatus && !shouldShowCompleted) return null

  const updatedAt = jobStatus?.updated_at
    ? new Date(jobStatus.updated_at).getTime()
    : null
  const isStalled =
    jobStatus &&
    (jobStatus.status === 'queued' || jobStatus.status === 'processing') &&
    updatedAt !== null &&
    Date.now() - updatedAt > 2 * 60 * 1000

  return (
    <div className="mb-4 rounded-lg border border-base-300 bg-base-100 p-4 shadow-sm">
      {jobStatus && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-semibold text-base-content">
              CSV import in progress
            </div>
            <div className="text-xs text-base-content/60">
              {jobStatus.processed_rows} of {jobStatus.total_rows} processed
            </div>
          </div>
          <progress
            className="progress progress-primary w-full mt-2"
            value={jobStatus.processed_rows}
            max={Math.max(1, jobStatus.total_rows)}
          />
          <div className="mt-2 text-xs text-base-content/60">
            {jobStatus.added} added · {jobStatus.skipped} skipped ·{' '}
            {jobStatus.failed} failed
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-base-content/70">
            <span>
              {isStalled
                ? 'Import looks stalled.'
                : 'Need to restart this import?'}
            </span>
            <button
              type="button"
              className="btn btn-outline btn-xs"
              disabled={resumeLoading}
              onClick={async () => {
                if (!jobStatus) return
                setResumeMessage(null)
                setResumeLoading(true)
                try {
                  const {
                    data: { session },
                  } = await supabase.auth.getSession()
                  const accessToken = session?.access_token
                  if (!accessToken) {
                    setResumeMessage('Missing session. Please log in again.')
                    return
                  }
                  const response = await fetch(
                    `${API_BASE_URL}/api/csv-imports/${jobStatus.id}/retry`,
                    {
                      method: 'POST',
                      headers: {
                        Authorization: `Bearer ${accessToken}`,
                      },
                    }
                  )
                  if (!response.ok) {
                    const errorPayload = await response.json().catch(() => null)
                    setResumeMessage(
                      errorPayload?.error ||
                        `Retry failed (status ${response.status})`
                    )
                    return
                  }
                  setResumeMessage('Retry started.')
                } catch (error) {
                  console.warn('[csv-import] retry failed', error)
                  setResumeMessage('Retry failed. See console for details.')
                } finally {
                  setResumeLoading(false)
                }
              }}
            >
              {resumeLoading ? 'Retrying...' : 'Retry import'}
            </button>
            {isStalled ? (
              <button
                type="button"
                className="btn btn-ghost btn-xs"
                onClick={async () => {
                  if (!jobStatus) return
                  setResumeMessage(null)
                  setResumeLoading(true)
                  try {
                    const {
                      data: { session },
                    } = await supabase.auth.getSession()
                    const accessToken = session?.access_token
                    if (!accessToken) {
                      setResumeMessage('Missing session. Please log in again.')
                      return
                    }
                    const response = await fetch(
                      `${API_BASE_URL}/api/csv-imports/${jobStatus.id}/cancel`,
                      {
                        method: 'POST',
                        headers: {
                          Authorization: `Bearer ${accessToken}`,
                        },
                      }
                    )
                    if (!response.ok) {
                      const errorPayload = await response.json().catch(() => null)
                      setResumeMessage(
                        errorPayload?.error ||
                          `Cancel failed (status ${response.status})`
                      )
                      return
                    }
                    setResumeMessage('Import canceled.')
                    handleDismiss(jobStatus.id, false)
                  } catch (error) {
                    console.warn('[csv-import] cancel failed', error)
                    setResumeMessage('Cancel failed. See console for details.')
                  } finally {
                    setResumeLoading(false)
                  }
                }}
              >
                Cancel import
              </button>
            ) : null}
          </div>
          {resumeMessage && (
            <div className="mt-2 text-xs text-base-content/60">
              {resumeMessage}
            </div>
          )}
        </>
      )}

      {shouldShowCompleted && completedJob && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-semibold text-base-content">
              CSV import {completedJob.status === 'failed' ? 'failed' : 'completed'}
            </div>
            <button
              type="button"
              className="btn btn-outline btn-xs"
              onClick={() => handleDismiss(completedJob?.id, true)}
            >
              Dismiss widget
            </button>
          </div>
          <div className="mt-2 text-xs text-base-content/70">
            {completedJob.added} added · {completedJob.skipped} skipped ·{' '}
            {completedJob.failed} failed
          </div>
          {completedJob.failed > 0 && (
            <div className="mt-3">
              <button
                type="button"
                className="btn btn-outline btn-xs"
                onClick={handleOpenReview}
                disabled={errorsLoading}
              >
                {errorsLoading
                  ? 'Loading errors...'
                  : 'Review errors'}
              </button>
            </div>
          )}
          {/* <div className="mt-3">
            <button
              type="button"
              className="btn btn-ghost btn-xs"
              onClick={handleDismiss}
            >
              Dismiss widget
            </button>
          </div> */}
        </>
      )}
      {showReviewModal && completedJob && (
        <div className="modal modal-open">
          <div className="modal-box max-w-3xl bg-base-100">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-lg font-semibold">Review import errors</h3>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setShowReviewModal(false)}
              >
                Close
              </button>
            </div>
            <p className="text-sm text-base-content/70 mt-2">
              Search IGDB and select the correct match for each row.
            </p>

            <div className="mt-4 space-y-4 max-h-[60vh] overflow-y-auto">
              {errorsLoading && <div>Loading errors...</div>}
              {!errorsLoading && (!errors || errors.length === 0) && (
                <div>No errors to review.</div>
              )}
              {errors &&
                errors.map((errorItem) => (
                  <div
                    key={errorItem.id}
                    className="rounded border border-base-300 bg-base-200 p-3"
                  >
                    <div className="text-sm font-semibold">
                      Row {errorItem.row_index}
                      {errorItem.title ? ` · ${errorItem.title}` : ''}
                    </div>
                    <div className="text-xs text-base-content/70">
                      {errorItem.reason || 'Unknown error'}
                    </div>

                    {errorItem.reason?.toLowerCase().startsWith('skipped') ? (
                      <div className="mt-2 text-xs text-base-content/60">
                        This game already exists in the user’s library.
                      </div>
                    ) : null}

                    {Array.isArray(errorItem.raw_row) ? null : errorItem.raw_row
                      ?.candidates?.length ? (
                      <div className="mt-3 space-y-2">
                        <div className="text-xs text-base-content/60">
                          Suggested matches:
                        </div>
                        {errorItem.raw_row.candidates.map((candidate) => (
                          <div
                            key={candidate.id}
                            className="flex flex-wrap items-center justify-between gap-2 rounded border border-base-300 bg-base-100 p-2"
                          >
                            <div className="flex items-center gap-2 text-xs">
                              {toCoverUrl(candidate.coverUrl) ? (
                                <img
                                  src={toCoverUrl(candidate.coverUrl) || undefined}
                                  alt={candidate.name}
                                  className="h-10 w-8 rounded object-cover bg-base-200"
                                  loading="lazy"
                                />
                              ) : null}
                              <div>
                                <div>
                                  {candidate.name}
                                  {candidate.first_release_date
                                    ? ` (${new Date(
                                        candidate.first_release_date * 1000
                                      ).getFullYear()})`
                                    : ''}
                                </div>
                                {candidate.score !== undefined ? (
                                  <div className="text-base-content/60">
                                    Match score: {candidate.score}%
                                  </div>
                                ) : null}
                              </div>
                            </div>
                            <button
                              type="button"
                              className="btn btn-outline btn-xs"
                              onClick={() => handleResolve(errorItem, candidate.id)}
                              disabled={!!resolveLoading[errorItem.id]}
                            >
                              {resolveLoading[errorItem.id]
                                ? 'Saving...'
                                : 'Use this match'}
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : null}

                    {!errorItem.reason?.toLowerCase().startsWith('skipped') && (
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <input
                          type="text"
                          className="input input-bordered input-sm flex-1 min-w-[200px]"
                          placeholder="Search IGDB..."
                          value={
                            searchQueries[errorItem.id] ?? errorItem.title ?? ''
                          }
                          onChange={(event) =>
                            setSearchQueries((prev) => ({
                              ...prev,
                              [errorItem.id]: event.target.value,
                            }))
                          }
                        />
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={() =>
                            handleSearch(errorItem.id, errorItem.title)
                          }
                          disabled={!!searchLoading[errorItem.id]}
                        >
                          {searchLoading[errorItem.id]
                            ? 'Searching...'
                            : 'Search'}
                        </button>
                      </div>
                    )}

                    {!errorItem.reason?.toLowerCase().startsWith('skipped') &&
                    searchResults[errorItem.id]?.length ? (
                      <div className="mt-3 space-y-2">
                        {searchResults[errorItem.id].map((result) => (
                          <div
                            key={result.id}
                            className="flex flex-wrap items-center justify-between gap-2 rounded border border-base-300 bg-base-100 p-2"
                          >
                            <div className="flex items-center gap-2 text-xs">
                              {toCoverUrl(result.cover?.url) ? (
                                <img
                                  src={toCoverUrl(result.cover?.url) || undefined}
                                  alt={result.name}
                                  className="h-10 w-8 rounded object-cover bg-base-200"
                                  loading="lazy"
                                />
                              ) : null}
                              <div>
                                <div>
                                  {result.name}
                                  {result.first_release_date
                                    ? ` (${new Date(
                                        result.first_release_date * 1000
                                      ).getFullYear()})`
                                    : ''}
                                </div>
                              </div>
                            </div>
                            <button
                              type="button"
                              className="btn btn-outline btn-xs"
                              onClick={() => handleResolve(errorItem, result.id)}
                              disabled={!!resolveLoading[errorItem.id]}
                            >
                              {resolveLoading[errorItem.id]
                                ? 'Saving...'
                                : 'Use this match'}
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : null}
                    {!errorItem.reason?.toLowerCase().startsWith('skipped') &&
                      searchAttempts[errorItem.id] &&
                      !searchLoading[errorItem.id] &&
                      !searchErrors[errorItem.id] &&
                      (!searchResults[errorItem.id] ||
                        searchResults[errorItem.id].length === 0) && (
                        <div className="mt-2 text-xs text-base-content/60">
                          No matches found. Try a different search.
                        </div>
                      )}
                    {!errorItem.reason?.toLowerCase().startsWith('skipped') &&
                      searchErrors[errorItem.id] && (
                      <div className="mt-2 text-xs text-error">
                        {searchErrors[errorItem.id]}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => setShowReviewModal(false)}
          ></div>
        </div>
      )}
    </div>
  )
}

export default CsvImportStatusWidget
